import { IPartnerUserRegistrationData } from '@/service/partners/api';
import { usePartnerLogin, usePartnerRegister } from '@/service/partners/hooks';
import { CheckIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Card,
  Checkbox,
  Container,
  FormControl,
  Heading,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaSignInAlt, FaUserPlus } from 'react-icons/fa';
import { z } from 'zod';
import { Button } from '../../../components/atoms/Button';
import { Input } from '../../../components/atoms/Input';
import { useTheme } from '../../../contexts/ThemeProvider';
import { capturePartnerMessage } from '../utils/sentryUtils';

// Esquemas de validação simplificados
const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Senha é obrigatória'),
});

const registerSchema = z
  .object({
    full_name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
    email: z.string().email('Email inválido'),
    password: z
      .string()
      .min(8, 'Senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
      .regex(/\d/, 'Senha deve conter pelo menos um número')
      .regex(/[@$!%*?&]/, 'Senha deve conter pelo menos um caractere especial (@$!%*?&)'),
    confirmPassword: z.string(),
    termsAccepted: z.boolean().refine((val) => val === true, 'Você deve aceitar os termos'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Senhas não coincidem',
    path: ['confirmPassword'],
  });

type LoginFormData = z.infer<typeof loginSchema>;
type RegisterFormData = z.infer<typeof registerSchema>;

interface StepPartnerRegistrationProps {
  onNext: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateData: (data: any) => void;
}

export function StepPartnerRegistration({ onNext, data, updateData }: StepPartnerRegistrationProps) {
  const { colors, id } = useTheme();
  const [tabIndex, setTabIndex] = useState(0); // Registro em destaque inicial

  const {
    handleSubmit: handleSubmitLogin,
    formState: { errors: loginErrors },
    setError: setLoginError,
    control: controlLogin,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: data?.user?.email || '',
      password: '',
    },
  });

  // Hooks para formulário de registro
  const {
    setValue,
    handleSubmit: handleSubmitRegister,
    formState: { errors: registerErrors },
    setError: setRegisterError,
    control: controlRegister,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      full_name: data?.user?.full_name || '',
      email: data?.user?.email || '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },
  });

  const watchedPassword = watch('password');

  const partnerLogin = usePartnerLogin({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (loginData: any) => {
      updateData({
        user: loginData.user,
        isNewUser: loginData.isNewUser,
        token: loginData.token,
        company: loginData.company,
        store: loginData.store,
      });
      onNext();
    },
    onError: (error: string) => {
      // Capturar erro no Sentry
      capturePartnerMessage(`Login error in StepPartnerRegistration: ${error}`, {
        component: 'StepPartnerRegistration',
        step: 'registration',
        action: 'login',
      });
      setLoginError('root', { message: error });
    },
  });

  const partnerRegister = usePartnerRegister({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (registerData: any) => {
      updateData({
        user: registerData.user,
        isNewUser: registerData.isNewUser,
      });
      onNext();
    },
    onError: (error: string) => {
      // Capturar erro no Sentry
      capturePartnerMessage(`Register error in StepPartnerRegistration: ${error}`, {
        component: 'StepPartnerRegistration',
        step: 'registration',
        action: 'register',
      });
      setRegisterError('root', { message: error });
    },
  });

  const handleLogin = (formData: LoginFormData) => {
    partnerLogin.mutate({
      email: formData.email,
      password: formData.password,
    });
  };

  const handleRegister = async (formData: RegisterFormData) => {
    const registrationData: IPartnerUserRegistrationData = {
      full_name: formData.full_name,
      email: formData.email,
      password: formData.password,
    };

    partnerRegister.mutate({ data: registrationData, agreementId: id?.toString() || '' });
  };

  const getPasswordValidation = (password: string) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
    };
    return validations;
  };

  return (
    <Container width={'100%'}>
      <VStack spacing={6}>
        <Box textAlign="center">
          <Heading size="lg" color="gray.800" mb={2}>
            Autenticação
          </Heading>
          <Text color="gray.600">Faça login ou crie sua conta para continuar</Text>
        </Box>

        <Card w="full" shadow="lg" p={4}>
          <Tabs index={tabIndex} onChange={setTabIndex} variant="soft-rounded" colorScheme="blue">
            <TabList bg="gray.50" borderRadius="full" p={2} border="1px solid" borderColor="gray.200" gap={2} mb={6}>
              <Tab
                flex={1}
                borderRadius="full"
                fontSize="16px"
                fontWeight="600"
                fontFamily="Glory"
                color="gray.600"
                py={3}
                px={4}
                _selected={{
                  bg: 'white',
                  color: colors.primaryColor,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: colors.primaryColor,
                }}
                transition="all 0.2s"
              >
                <HStack spacing={2}>
                  <FaUserPlus size={16} />
                  <Text>Registrar</Text>
                </HStack>
              </Tab>
              <Tab
                flex={1}
                borderRadius="full"
                fontSize="16px"
                fontWeight="600"
                fontFamily="Glory"
                color="gray.600"
                py={3}
                px={4}
                _selected={{
                  bg: 'white',
                  color: colors.primaryColor,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: colors.primaryColor,
                }}
                transition="all 0.2s"
              >
                <HStack spacing={2}>
                  <FaSignInAlt size={16} />
                  <Text>Login</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Register Panel */}
              <TabPanel>
                <Box mb={4}>
                  <Alert status="info" borderRadius="md" bg="blue.50" borderColor="blue.200" border="1px">
                    <AlertIcon color="blue.500" />
                    <Box>
                      <AlertTitle fontWeight="bold" color="blue.800" mb={1}>
                        Já possui cadastro?
                      </AlertTitle>
                      <AlertDescription color="blue.700" fontSize="sm">
                        Se você já tem uma conta, clique na aba "Login" para continuar o processo de parceria de forma
                        mais rápida.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </Box>
                <form onSubmit={handleSubmitRegister(handleRegister)}>
                  <VStack spacing={4}>
                    <FormControl isInvalid={!!registerErrors.full_name}>
                      <Input
                        placeholder="Digite seu nome completo"
                        name="full_name"
                        label="Nome completo"
                        control={controlRegister}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!registerErrors.email}>
                      <Input
                        type="email"
                        placeholder="Digite seu email"
                        name="email"
                        label="Email"
                        control={controlRegister}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!registerErrors.password}>
                      <Input
                        type="password"
                        placeholder="Digite sua senha"
                        name="password"
                        label="Senha"
                        control={controlRegister}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!registerErrors.confirmPassword}>
                      <Input
                        type="password"
                        placeholder="Confirme sua senha"
                        name="confirmPassword"
                        label="Confirmar senha"
                        control={controlRegister}
                      />
                    </FormControl>

                    {/* Password Requirements */}
                    {watchedPassword && (
                      <Box w="full" p={4} bg="gray.50" borderRadius="md">
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                          A senha deve conter:
                        </Text>
                        <VStack align="start" spacing={1}>
                          {Object.entries(getPasswordValidation(watchedPassword)).map(([key, isValid]) => (
                            <HStack key={key} spacing={2}>
                              <CheckIcon color={isValid ? 'green.500' : 'gray.300'} boxSize={3} />
                              <Text fontSize="xs" color={isValid ? 'green.600' : 'gray.500'}>
                                {key === 'length' && 'Pelo menos 8 caracteres'}
                                {key === 'uppercase' && 'Uma letra maiúscula (A-Z)'}
                                {key === 'lowercase' && 'Uma letra minúscula (a-z)'}
                                {key === 'number' && 'Um número (0-9)'}
                                {key === 'special' && 'Um caractere especial (@$!%*?&)'}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    )}

                    {/* Terms and Conditions */}
                    <FormControl isInvalid={!!registerErrors.termsAccepted}>
                      <Checkbox colorScheme="blue" onChange={(e) => setValue('termsAccepted', e.target.checked)}>
                        <Text fontSize="sm" color="gray.600">
                          Aceito os{' '}
                          <Text
                            as="a"
                            href="/legal/termos-de-uso"
                            target="_blank"
                            color="blue.500"
                            textDecoration="underline"
                          >
                            Termos de Uso
                          </Text>{' '}
                          e{' '}
                          <Text
                            as="a"
                            href="/legal/politica-de-privacidade"
                            target="_blank"
                            color="blue.500"
                            textDecoration="underline"
                          >
                            Política de Privacidade
                          </Text>
                        </Text>
                      </Checkbox>
                    </FormControl>

                    {registerErrors.root && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertTitle>Erro!</AlertTitle>
                        <AlertDescription>{registerErrors.root.message}</AlertDescription>
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      colorScheme="blue"
                      w="full"
                      isLoading={partnerRegister.isLoading}
                      loadingText="Criando conta..."
                    >
                      Criar conta
                    </Button>
                  </VStack>
                </form>
              </TabPanel>

              {/* Login Panel */}
              <TabPanel>
                <form onSubmit={handleSubmitLogin(handleLogin)}>
                  <VStack spacing={4}>
                    <FormControl isInvalid={!!loginErrors.email}>
                      <Input
                        type="email"
                        label="Email"
                        placeholder="Digite seu email"
                        name="email"
                        control={controlLogin}
                      />
                    </FormControl>

                    <FormControl isInvalid={!!loginErrors.password}>
                      <Input
                        type="password"
                        label="Senha"
                        placeholder="Digite sua senha"
                        name="password"
                        control={controlLogin}
                      />
                    </FormControl>

                    {loginErrors.root && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <AlertTitle>Erro!</AlertTitle>
                        <AlertDescription>{loginErrors.root.message}</AlertDescription>
                      </Alert>
                    )}

                    <Button type="submit" isLoading={partnerLogin.isLoading}>
                      {partnerLogin.isLoading ? 'Entrando...' : 'Entrar'}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      color={'#5e5e5b'}
                      border="none"
                      backgroundColor="white"
                      onClick={() => {
                        window.open(`https://api.edge.partiu.com.br/password_reset/?agreement_id=${id}`, '_blank');
                      }}
                    >
                      Esqueci minha senha
                    </Button>
                  </VStack>
                </form>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>
      </VStack>
    </Container>
  );
}
